import { useEffect, useState } from "react";
import {
	CardBody,
	Table,
	Button,
	Input,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from "reactstrap";
import { Search } from "react-feather";
import Swal from "sweetalert2";
import {
	attachDetachTryoutMemberships,
	fetchMemberships,
} from "Features/Learning/Memberships/Utils/MembershipUtils";
import { setDetail as setDetailMembership } from "Features/Learning/Memberships/membershipSlice";
import {
	fetchTryouts,
	handleBackPagination,
	handleNextPagination,
	handleClickPagination,
} from "Component/Learning/Tryout/TryoutUtils";
import { setFilters } from "Features/Learning/Tryout/tryoutSlice";
import PaginationComponent from "Component/Learning/Common/Pagination";
import { useSelector, useDispatch } from "react-redux";

const AttachTryout = ({ items, membershipId }) => {
	const dispatch = useDispatch();
	const filters = useSelector((state) => state.Tryout.filters);
	const filter = useSelector((state) => state.Membership.filters);
	const [modal, setModal] = useState(false);
	const [selectedItems, setSelectedItems] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const tryoutSearchResult = useSelector((state) => state.Tryout.data.data);
	const isLoading = useSelector((state) => state.Tryout.isLoading);
	const pagination = useSelector((state) => state.Tryout.data.pagination);
	const [refatch, setRefatch] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		fetchTryouts(dispatch, filters);
	}, [dispatch, filters]);

	useEffect(() => {
		if (items?.length > 0) {
			setSelectedItems(items);
			setSelectedIds(items?.map((item) => item.id));
		}
	}, [items]);

	// close modal tanpa konfirmasi (dipakai setelah submit sukses)
	const closeModal = () => {
		const itemsArr = Array.isArray(items) ? items : [];
		setSelectedItems(itemsArr.length > 0 ? itemsArr : []);
		setSelectedIds(itemsArr.length > 0 ? itemsArr.map((it) => it.id) : []);
		setModal(false);
	};

	const toggle = async () => {
		const initialIds = (items || []).map((i) => i.id).sort().join(",");
		const currentIds = (selectedIds || []).slice().sort().join(",");

		if (modal) {
			// jika ada perubahan, minta konfirmasi
			if (initialIds !== currentIds) {
				const result = await Swal.fire({
					title: "Apakah anda yakin?",
					text: "Perubahan yang belum disimpan akan dibatalkan.",
					icon: "warning",
					showCancelButton: true,
					confirmButtonColor: "#3085d6",
					confirmButtonText: "Ya",
					cancelButtonColor: "#d33",
				});

				if (!result.isConfirmed) return;
			}

			// restore to initial and close using closeModal helper
			closeModal();
			return;
		}

		// opening modal: inisialisasi selected dari props lalu buka modal
		setSelectedItems(items?.length > 0 ? items : []);
		setSelectedIds(items?.length > 0 ? items.map((it) => it.id) : []);
		setModal(true);
	};

	const handleCheckboxChange = (item) => {
		if (selectedIds.includes(item.id)) {
			const newSelectedItems = selectedItems.filter((value) => value.id !== item.id);
			const newSelectedIds = selectedIds.filter((id) => id !== item.id);
			setSelectedItems(newSelectedItems);
			setSelectedIds(newSelectedIds);
		} else {
			setSelectedItems([...selectedItems, item]);
			setSelectedIds([...selectedIds, item.id]);
		}
	};

	const handleClickSearch = () => {
		setRefatch(!refatch);
	};

	const handleSubmit = async () => {
		if (isSubmitting) return;
		setIsSubmitting(true);
		try {
			const newTryouts = selectedItems
				.filter(
					(item, index, self) => self.findIndex((t) => t.id === item.id) === index
				)
				.map((item) => {
					return {
						value: item.id,
					};
				});
			const data = {
				tryouts: newTryouts,
				id: membershipId,
			};
			setIsSubmitting(true);
			try {
				const response = await dispatch(attachDetachTryoutMemberships(data));

				if (response.payload === true) {
					// close without confirmation because this is a programmatic successful save
					closeModal();
					const getMembership = await dispatch(
						fetchMemberships({
							...filter,
							"filter[id]": membershipId,
							sort: "",
							page: "",
							"include": "courses,tryouts,mainProgram,tryouts.category"
						})
					);

					dispatch(setDetailMembership(getMembership.payload.data[0]));
					Swal.fire("Berhasil!", "Tryout Berhasil Ditambahkan", "success");
				}
			} catch (error) {
				console.error("Error:", error);
			}
		} catch (error) {
			console.error("Error:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<Button color="primary" onClick={toggle}>
				<i className={"fa fa-plus-square fa-lg me-2"}></i>
				Tambah Tryout
			</Button>{" "}
			<Modal isOpen={modal}>
				<ModalHeader onClick={toggle}>
					Tambahkan Tryout ke Membership
				</ModalHeader>
				<ModalBody>
					<ul>
						{selectedItems.length > 0 &&
							selectedItems.map((item, index) => (
								<li key={`item-${index}`}>
									{item.id} - {item.title}
								</li>
							))}
					</ul>
					<div className="faq-form mt-3">
						<Input
							className="form-control"
							type="text"
							placeholder="Search.."
							value={filters["filter[title]"]}
							onKeyUp={(e) => (e.key === "Enter" ? handleClickSearch(e) : null)}
							onChange={(e) => {
								dispatch(
									setFilters({
										...filters,
										"filter[title]": e.target.value,
									})
								);
							}}
						/>
						<Search className="search-icon" />
					</div>
					<Table className="table-responsive table-bordered my-3">
						<thead className="table-light">
							<tr>
								<th scope="col">{"#"}</th>
								<th scope="col">{"ID"}</th>
								<th scope="col">Nama Try out</th>
								<th scope="col" className="text-center" colSpan="2">
									{"Aksi"}
								</th>
							</tr>
						</thead>
						<tbody>
							{tryoutSearchResult &&
								tryoutSearchResult.map((item, index) => (
									<tr key={`item-${index}`}>
										<th>{index + 1}</th>
										<th>{item.id}</th>
										<td>{item.title}</td>

										<td className="text-center">
											<Input
												className="checkbox_animated"
												id={`detach-${item.id}`}
												type="checkbox"
												checked={selectedIds.includes(item.id)}
												onChange={() => handleCheckboxChange(item)}
											/>
										</td>
									</tr>
								))}
						</tbody>
					</Table>
					<CardBody>
						{isLoading === false && pagination && (
							<PaginationComponent
								pagination={pagination}
								handleBackPagination={() =>
									handleBackPagination(
										pagination,
										filters,
										dispatch,
										setFilters,
										setRefatch
									)
								}
								handleClickPagination={(page) =>
									handleClickPagination(
										page,
										filters,
										dispatch,
										setFilters,
										setRefatch
									)
								}
								handleNextPagination={() =>
									handleNextPagination(
										pagination,
										filters,
										dispatch,
										setFilters,
										setRefatch
									)
								}
							/>
						)}
					</CardBody>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={toggle}>
						Cancel
					</Button>
					<Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
						Simpan
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default AttachTryout;